import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import { Typography, Input, Select, Checkbox } from 'antd';
import { useField } from 'formik';
import classnames from 'classnames';
import countries from 'common/constants/countries';

const useStyles = makeStyles(
  {
    inputErr: {
      borderColor: '#F25202 !important',

      '&:hover': {
        borderColor: '#F25202 !important',
        boxShadow: 'none !important',
      },

      '& > input': {
        borderColor: '#F25202 !important',
        boxShadow: 'none !important',
      },
    },

    inputSelectErr: {
      borderColor: '#F25202 !important',

      '& > * span': {
        borderColor: '#F25202 !important',
        boxShadow: 'none !important',

        '&:hover': {
          borderColor: '#F25202 !important',
          boxShadow: 'none !important',
        },
      },

      '& > * input': {
        borderColor: '#F25202 !important',
        boxShadow: 'none !important',

        '&:hover': {
          borderColor: '#F25202 !important',
          boxShadow: 'none !important',
        },
      },
    },
    checkbox: {
      display: 'flex',
      fontSize: '12px !important',
      lineHeight: '22px !important',
      color: '#595959 !important',
      marginTop: '16px !important',

      '& a': {
        textDecoration: 'underline',
      },

      '& > *:first-child': {
        width: 16,
        height: 16,
        marginTop: 4,
      },
    },
  },
  { name: 'loginFormikFields' },
);

const phoneNumberErrorTypeCodes = {
  INVALID_PHONE: 'INVALID_PHONE',
  SEND_PHONE_CODE_LIMIT_ERROR: 'SEND_PHONE_CODE_LIMIT_ERROR',
};

export const getPhoneNumberModalErrorTip = errMassageText => {
  if (errMassageText.includes(phoneNumberErrorTypeCodes.INVALID_PHONE)) {
    return 'please_enter_a_valid_phone_number';
  }
  if (errMassageText.includes(phoneNumberErrorTypeCodes.SEND_PHONE_CODE_LIMIT_ERROR)) {
    return 'sms_failed_to_send_you_may_have_reached_the_request_limit_or_entered_a_wrong_number_please_check_the_number_and_try_again_later';
  }
};

const useHelperTextStyles = makeStyles({
  errText: {
    textAlign: 'left',
    color: '#F25202',
    marginTop: 8,
  },
});

const FieldHelperText = ({ meta }) => {
  const classes = useHelperTextStyles();
  const errorText = meta.touched && meta.error;
  return (
    <Typography
      className={classes.errText}
      style={{ marginTop: `${errorText ? 4 : 0}px`, fontSize: '12px' }}
    >
      {errorText}
    </Typography>
  );
};

FieldHelperText.propTypes = {
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.any,
  }),
};

const SelectPhoneNumberCode = ({ fieldConfig, handleSelectFocus }) => {
  const classes = useStyles();
  const [field, meta, helpers] = useField(fieldConfig);

  return (
    <Select
      {...field}
      style={{ width: 96 }}
      onChange={e => {
        helpers.setValue(e);
      }}
      onFocus={handleSelectFocus}
      value={field?.value}
      className={meta.touched && meta.error ? classes.inputSelectErr : ''}
    >
      <For each="item" of={countries.list}>
        <Select.Option value={item.phoneCode} key={item.slug}>
          {item.phoneCode}
        </Select.Option>
      </For>
    </Select>
  );
};

SelectPhoneNumberCode.propTypes = {
  fieldConfig: PropTypes.string.isRequired,
  handleSelectFocus: PropTypes.func.isRequired,
};

export const FieldSelectPhoneNumber = ({
  fieldConfig,
  className,
  phoneCodeFieldConfig,
  onSelectFocus,
  ...otherProps
}) => {
  const classes = useStyles();
  const [field, meta] = useField(fieldConfig);

  return (
    <>
      <Input
        {...otherProps}
        {...field}
        addonBefore={
          <SelectPhoneNumberCode
            fieldConfig={phoneCodeFieldConfig}
            handleSelectFocus={onSelectFocus}
          />
        }
        className={classnames(meta.touched && meta.error ? classes.inputSelectErr : '', className)}
      />

      <FieldHelperText meta={meta} />
    </>
  );
};

FieldSelectPhoneNumber.propTypes = {
  phoneCodeFieldConfig: PropTypes.string.isRequired,
  fieldConfig: PropTypes.string.isRequired,
  className: PropTypes.string,
  onSelectFocus: PropTypes.func.isRequired,
};

export const FieldCheckbox = ({ fieldConfig, className, label, ...otherProps }) => {
  const classes = useStyles();
  const [field, meta] = useField(fieldConfig);

  return (
    <>
      <Checkbox
        {...field}
        {...otherProps}
        checked={field.value}
        className={classnames(classes.checkbox, className)}
      >
        {label}
      </Checkbox>
      <FieldHelperText meta={meta} />
    </>
  );
};

FieldCheckbox.propTypes = {
  fieldConfig: PropTypes.string.isRequired,
  className: PropTypes.string,
};

const useFieldInputStyles = makeStyles({
  errText: {
    textAlign: 'left',
    color: '#F25202',
  },
  inputErr: {
    borderColor: '#F25202 !important',

    '&:hover': {
      borderColor: '#F25202 !important',
      boxShadow: 'none !important',
    },
  },
});

export const FieldInput = props => {
  const { fieldConfig, className, ...otherProps } = props;
  const [field, meta] = useField(fieldConfig);
  const classes = useFieldInputStyles();

  return (
    <>
      <Input
        {...field}
        size="large"
        className={classnames(meta.touched && meta.error ? classes.inputErr : '', className)}
        {...otherProps}
      />
      <FieldHelperText meta={meta} />
    </>
  );
};

FieldInput.propTypes = {
  errMassage: PropTypes.string,
  fieldConfig: PropTypes.string,
  className: PropTypes.string,
};

export default FieldInput;
